import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import {
  getMemberships,
  getMembershipsForSiteLocation,
  getMembershipTypes
} from '@gym-graphql/queries';
import { Membership, MembershipInfo, MembershipSite, MembershipType } from '@gym-src/API';
import {
  MembershipSite as typeOfMembershipSite,
  PaginatedQueryParams
} from '@gym-particles/types/models';
import { MemberShipType as typesOfMembership } from '@gym-particles/types/MembershipType';
import { MembershipSiteLocation as typeOfMembershipSiteLocation } from '@gym-particles/types/Membership';
import i18n from '@gym-particles/i18next';
import { deleteMembership as deleteMembershipMutation } from '@gym-graphql/mutations';
import { AccessGroupLink } from '@gym-particles/types/MembershipSite';

dayjs.extend(relativeTime);

const initialState: {
  isMembershipsLoading: boolean;
  isMembershipTypesLoading: boolean;
  isMembershipSiteLocationLoading: boolean;
  membershipTypes: Array<typesOfMembership>;
  membershipsForSite: Array<{
    chainId: number;
    items: typeOfMembershipSite[];
    totalRecords: number;
  }>;
  membershipSiteLocation: Array<{
    siteLocationId: number;
    items: typeOfMembershipSiteLocation[];
    totalRecords: number;
  }>;
} = {
  isMembershipsLoading: false,
  isMembershipTypesLoading: false,
  isMembershipSiteLocationLoading: false,
  membershipTypes: [],
  membershipsForSite: [],
  membershipSiteLocation: []
};

type MembershipsForSiteParams = {
  siteId: number;
  pagination: PaginatedQueryParams;
  isMindbody: boolean;
};
type MembershipsForSiteResult =
  | { kind: 'loaded'; items: typeOfMembershipSite[]; totalRecords: number }
  | { kind: 'unavailable' };
type membershipsForSiteLocationParamType = {
  siteLocationId: number;
  pagination: PaginatedQueryParams;
};

const getAccessGroupLinkLabel = (
  hasMembershipAccessCategory: boolean,
  hasProgramAccessCategory: boolean
) => {
  if (hasMembershipAccessCategory && hasProgramAccessCategory) {
    return i18n.t('MEMBERSHIP_LIST.TABLE.ACCESS_GROUP_LINK_MEMBERSHIP_AND_PROGRAM');
  }
  if (hasMembershipAccessCategory) {
    return i18n.t('MEMBERSHIP_LIST.TABLE.ACCESS_GROUP_LINK_MEMBERSHIP');
  }
  if (hasProgramAccessCategory) {
    return i18n.t('MEMBERSHIP_LIST.TABLE.ACCESS_GROUP_LINK_PROGRAM');
  }
  return i18n.t('COMMON.NO');
};

const getAccessGroupLinks = (
  hasMembershipAccessCategory: boolean,
  hasProgramAccessCategory: boolean
): AccessGroupLink[] => {
  const includeLink = (include: boolean, link: AccessGroupLink): AccessGroupLink[] =>
    include ? [link] : [];

  return [
    ...includeLink(hasMembershipAccessCategory, 'membership'),
    ...includeLink(hasProgramAccessCategory, 'program')
  ];
};

const parseMembershipSite = (membership: MembershipSite): typeOfMembershipSite => ({
  id: membership.id || 0,
  siteId: membership.siteId || 0,
  membershipName: membership.membershipName || '',
  membershipTypeId: membership.membershipTypeId || 0,
  membershipType: membership.membershipType || '',
  isImported: membership.isImported || false,
  importedId: membership.importedId,
  validityPeriod: membership.validityPeriod || 0,
  status: membership.status || '',
  siteLocations: membership.siteLocations || '',
  programAccessLocations: membership.programAccessLocations || '',
  program: membership.program || '',
  displayStatus: membership.status
    ? membership.status.charAt(0).toUpperCase() + membership.status.slice(1)
    : '',
  createdBy: membership.createdBy || 0,
  createdDate: membership.createdDate
    ? dayjs(membership.createdDate || '').format('DD MMM YYYY')
    : '',
  lastModifiedBy: membership.lastModifiedBy || 0,
  lastModifiedDate: membership.lastModifiedDate || '',
  displayIsImported: membership.isImported ? i18n.t('COMMON.YES') : i18n.t('COMMON.NO'),
  createdUserName: `${membership.firstName} ${membership.lastName}` || '',
  displayHasRelatedAccessCategory: getAccessGroupLinkLabel(
    Boolean(membership.hasRelatedAccessCategory),
    Boolean(membership.hasRelatedProgramAccessCategory)
  ),
  accessGroupLinks: getAccessGroupLinks(
    Boolean(membership.hasRelatedAccessCategory),
    Boolean(membership.hasRelatedProgramAccessCategory)
  ),
  scheduleType: membership.scheduleType || null,
  isContractOnly: membership.isContractOnly || false
});

export const loadMembershipsForSite = async (
  params: MembershipsForSiteParams
): Promise<MembershipsForSiteResult> => {
  const response = await (API.graphql({
    query: getMemberships,
    variables: {
      siteId: params.siteId,
      offset: params.pagination.offset,
      pageSize: params.pagination.pageSize,
      sortField: params.pagination.sortField,
      sortOrder: params.pagination.sortOrder === 1 ? 'ASC' : 'DESC',
      search: {
        searchField: params.pagination.search?.searchField,
        searchText: params.pagination.search?.searchText
      },
      isMindbody: params.isMindbody || false
    }
  }) as Promise<{
    data: {
      getMemberships: {
        items: MembershipSite[];
        totalRecords: { totalRecords: number };
      } | null;
    };
  }>);
  const memberships = response.data.getMemberships;

  return memberships
    ? {
        kind: 'loaded',
        items: memberships.items.map(parseMembershipSite),
        totalRecords: memberships.totalRecords.totalRecords
      }
    : { kind: 'unavailable' };
};

export const fetchMembershipsForSite = createAsyncThunk(
  'membership/getMemberships',
  async (params: MembershipsForSiteParams) => loadMembershipsForSite(params)
);

export const fetchMembershipsForSiteLocation = createAsyncThunk(
  'membership/getMembershipsBySiteLocationId',
  async (params: membershipsForSiteLocationParamType) => {
    const response = await (API.graphql({
      query: getMembershipsForSiteLocation,
      variables: {
        siteLocationId: params.siteLocationId,
        offset: params.pagination.offset,
        pageSize: params.pagination.pageSize,
        sortField: params.pagination.sortField,
        sortOrder: params.pagination.sortOrder === 1 ? 'ASC' : 'DESC',
        search: {
          searchField: params.pagination.search?.searchField,
          searchText: params.pagination.search?.searchText
        }
      }
    }) as Promise<{
      data: {
        getMembershipsForSiteLocation: {
          items: MembershipInfo[];
          totalRecords: { totalRecords: number };
        };
      };
    }>);
    return response;
  }
);

export const fetchMembershipTypes = createAsyncThunk(
  'membershipType/getMembershipTypes',
  async () => {
    const response = await (API.graphql({
      query: getMembershipTypes
    }) as Promise<{
      data: { getMembershipTypes: MembershipType[] };
    }>);
    return response;
  }
);

export const deleteMembership = createAsyncThunk(
  'SiteLocation/deleteMembership',
  async (args: { id: number; gymChainId: number }) => {
    const response = await (API.graphql({
      query: deleteMembershipMutation,
      variables: {
        id: args.id
      }
    }) as Promise<{
      data: { deleteMembership: Membership };
    }>);
    return response;
  }
);

export const membershipForSiteSlice = createSlice({
  name: 'membershipForSite',
  initialState,
  reducers: {
    toggleLoading: (state) => {
      state.isMembershipsLoading = !state.isMembershipsLoading;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMembershipsForSite.fulfilled, (state, action) => {
      state.isMembershipsLoading = false;
      if (action.payload.kind === 'unavailable') {
        return;
      }
      const { items, totalRecords } = action.payload;
      const chainId = action.meta.arg.siteId;
      const fetchedMembershipInfo = {
        chainId: chainId,
        totalRecords: totalRecords,
        items: items
      };
      if (state.membershipsForSite.length === 0) {
        state.membershipsForSite.push(fetchedMembershipInfo);
      } else {
        const update = state.membershipsForSite.find(
          (membershipInfo) => membershipInfo.chainId === fetchedMembershipInfo.chainId
        );
        if (update) {
          const newState = state.membershipsForSite.map((membershipInfo) => {
            if (membershipInfo.chainId === fetchedMembershipInfo.chainId) {
              return fetchedMembershipInfo;
            } else {
              return membershipInfo;
            }
          });
          Object.assign(state.membershipsForSite, newState);
        } else {
          state.membershipsForSite.push(fetchedMembershipInfo);
        }
      }
    });

    builder.addCase(fetchMembershipsForSite.pending, (state, action) => {
      state.isMembershipsLoading = true;
      return state;
    });

    builder.addCase(fetchMembershipsForSiteLocation.fulfilled, (state, action) => {
      state.isMembershipSiteLocationLoading = false;

      if (action.payload.data.getMembershipsForSiteLocation === null) {
        return;
      }
      const items = action.payload.data.getMembershipsForSiteLocation.items.map(
        (e: MembershipInfo): typeOfMembershipSiteLocation => {
          return {
            SiteLocationMembershipId: e.siteLocationMembershipId || 0,
            siteLocationId: e.siteLocationId || 0,
            membershipId: e.membershipId || 0,
            membershipName: e.membershipName || '',
            membershipTypeName: e.membershipTypeName || '',
            validityPeriod: e.validityPeriod || 0,
            isImported: e.isImported || false,
            importedId: e.importedId,
            status: e.status || '',
            createdDate: e.createdDate ? dayjs(e.createdDate || '').format('DD MMM YYYY') : '',
            displayValidityPeriod: e.validityPeriod
              ? e.validityPeriod > 30
                ? Math.floor(e.validityPeriod / 30) + ' Months'
                : e.validityPeriod + ' Days'
              : '',
            displayStatus: e.status ? e.status.charAt(0).toUpperCase() + e.status.slice(1) : '',
            displayIsImported: e.isImported ? i18n.t('COMMON.YES') : i18n.t('COMMON.NO')
          };
        }
      );
      const totalRecords =
        action.payload.data.getMembershipsForSiteLocation.totalRecords.totalRecords;

      const siteLocationId = action.meta.arg.siteLocationId;

      const fetchedMembershipSiteLocationInfo = {
        siteLocationId: siteLocationId,
        totalRecords: totalRecords,
        items: items
      };

      if (state.membershipSiteLocation.length === 0) {
        state.membershipSiteLocation.push(fetchedMembershipSiteLocationInfo);
      } else {
        const update = state.membershipSiteLocation.find(
          (membershipSiteLocationInfo) =>
            membershipSiteLocationInfo.siteLocationId ===
            fetchedMembershipSiteLocationInfo.siteLocationId
        );
        if (update) {
          const newState = state.membershipSiteLocation.map((membershipSiteLocationInfo) => {
            if (
              membershipSiteLocationInfo.siteLocationId ===
              fetchedMembershipSiteLocationInfo.siteLocationId
            ) {
              return fetchedMembershipSiteLocationInfo;
            } else {
              return membershipSiteLocationInfo;
            }
          });
          Object.assign(state.membershipSiteLocation, newState);
        } else {
          state.membershipSiteLocation.push(fetchedMembershipSiteLocationInfo);
        }
      }
    });

    builder.addCase(fetchMembershipsForSiteLocation.pending, (state, action) => {
      state.isMembershipSiteLocationLoading = true;
      return state;
    });

    builder.addCase(fetchMembershipsForSiteLocation.rejected, (state, action) => {
      state.isMembershipSiteLocationLoading = false;
      console.error('fetchMembershipsForSiteLocation', action);
      return state;
    });

    builder.addCase(fetchMembershipTypes.fulfilled, (state, action) => {
      state.isMembershipTypesLoading = false;
      if (action.payload.data.getMembershipTypes === null) {
        return;
      }
      const items = action.payload.data.getMembershipTypes.map(
        (e: MembershipType): typesOfMembership => {
          return {
            id: e.id || 0,
            name: e.name || ''
          };
        }
      );
      state.membershipTypes = items;
      return state;
    });

    builder.addCase(fetchMembershipTypes.pending, (state, action) => {
      state.isMembershipTypesLoading = true;
      return state;
    });
    builder.addCase(deleteMembership.fulfilled, (state, action) => {
      state.isMembershipsLoading = false;
      const membershipId = action.meta.arg.id;
      const gymChainMembership = state.membershipsForSite.find(
        (memberships) => memberships.chainId === action.meta.arg.gymChainId
      );
      if (!gymChainMembership) {
        return;
      }
      const filtered = gymChainMembership?.items.filter((membershipObj) => {
        return membershipObj.id !== membershipId;
      });

      gymChainMembership.items = filtered;
      const filteredMemberships = state.membershipsForSite.map((memberships) => {
        if (memberships.chainId === action.meta.arg.gymChainId) {
          return gymChainMembership;
        } else {
          return memberships;
        }
      });
      state.membershipsForSite = filteredMemberships;
    });
    builder.addCase(deleteMembership.pending, (state, action) => {
      state.isMembershipsLoading = true;
      return state;
    });
  }
});

export const { toggleLoading } = membershipForSiteSlice.actions;

export default membershipForSiteSlice.reducer;
